import type { BynderImage } from '@workspace/interfaces';

export interface PictureImageSource {
  alt?: string | null;
  bynderImage?: BynderImage | null;
}

export type PictureSource = PictureImageSource | BynderImage;

export const getBynderAsset = (image: PictureSource): BynderImage | undefined => {
  const withBynder = image as PictureImageSource;
  if (withBynder.bynderImage) return withBynder.bynderImage;

  const bynder = image as BynderImage;
  if (bynder._type === 'bynder.asset' || 'selectedUrl' in image || 'databaseId' in image) {
    return bynder;
  }

  return undefined;
};

export interface ResolvedSource {
  baseUrl: string;
  isBynder: boolean;
  isSvg: boolean;
}

export const resolveSource = (image: PictureSource): ResolvedSource => {
  const bynder = getBynderAsset(image);
  const bynderUrl = bynder?.selectedUrl ?? bynder?.url ?? bynder?.datUrl ?? bynder?.previewUrl ?? '';

  return { baseUrl: bynderUrl, isBynder: !!bynderUrl, isSvg: !!bynder?.isSvg };
};

export const resolveAlt = (image: PictureSource, alt?: string): string =>
  alt ?? getBynderAsset(image)?.alt ?? (image as PictureImageSource).alt ?? '';

/** Build a chained DAT transform URL for the given width/height. */
export const buildDatUrl = (baseUrl: string, width: number, height: number): string => {
  if (!baseUrl) return '';

  const aspectRatioTransform = `io=transform:fill,aspectratio:${width}x${height}`;
  const resizeTransform = `io=transform:fill,width:${width},height:${height}`;
  const transform = `${aspectRatioTransform}&${resizeTransform}`;

  if (/[?&]io=transform:[^&]*/.test(baseUrl)) {
    return baseUrl.replace(/([?&])io=transform:[^&]*/, `$1${transform}`);
  }

  const joiner = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${joiner}${transform}`;
};

/**
 * Build a density-descriptor srcset string (`1x` / `2x`) for retina displays.
 * For non-transformable images (SVG or plain URL), returns the baseUrl as-is.
 */
export const buildSrcSet = (
  baseUrl: string,
  width: number,
  height: number,
  shouldTransform: boolean,
): string => {
  if (!baseUrl) return '';
  if (!shouldTransform) return baseUrl;

  return `${buildDatUrl(baseUrl, width, height)} 1x, ${buildDatUrl(baseUrl, width * 2, height * 2)} 2x`;
};

/** Candidate widths for fluid images, whose rendered width is only known via `sizes` (e.g. full-bleed). */
const FLUID_SRCSET_WIDTHS = [480, 640, 828, 1080, 1280, 1600, 1920, 2560];

/**
 * Build a width-descriptor srcset (`480w, 640w, ...`) for fluid images, so the browser picks a candidate from the
 * rendered size (via `sizes`) and device pixel ratio. Every candidate keeps the aspect ratio of `width`x`height`.
 * For non-transformable images (SVG or plain URL), returns the baseUrl as-is.
 */
export const buildWidthSrcSet = (baseUrl: string, width: number, height: number, shouldTransform: boolean): string => {
  if (!baseUrl) return '';
  if (!shouldTransform) return baseUrl;

  return FLUID_SRCSET_WIDTHS.map(
    (candidateWidth) =>
      `${buildDatUrl(baseUrl, candidateWidth, Math.round((candidateWidth * height) / width))} ${candidateWidth}w`,
  ).join(', ');
};
